import Button from '@/components/Button/Button';
import EditorialErrors from '@/components/EditorialErrors/EditorialErrors';
import ArrowLeftIcon from '@/components/Icon/ArrowLeftIcon';
import CurrentDateContext from '@/context/CurrentDateContext';
import { useBackup } from '@/hooks/backups/useBackup';
import useRestore from '@/hooks/backups/useRestore';
import { useCurrentNativeVersion } from '@/hooks/native/useIsNative';
import useDate from '@/hooks/useDate';
import useIntro from '@/hooks/useIntro';
import { FC, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationsTest from './NotificationsTest';

const OptiesRoute: FC = () => {
	const navigate = useNavigate();
	const { setHasSeenIntro } = useIntro();
	const date = useDate();
	const version = useCurrentNativeVersion();

	return (
		<main className="grid min-h-full gap-4 divide-y-2 divide-dotted overflow-y-auto bg-gray-50 p-4">
			<div className="flex gap-4">
				<a
					href="/"
					onClick={(event) => {
						event.preventDefault();
						navigate(-1);
					}}
					className="flex gap-4"
					aria-label="Terug">
					<ArrowLeftIcon />
				</a>
				<h1 className="font-brand text-4xl">Opties</h1>
			</div>
			<p>
				Datum: {date}. Versie: {version ?? 'web'}.
			</p>
			<ChangeDateOptions />
			<BackupOptions />
			<RestoreOptions />
			<NotificationsTest />
			<div>
				<p>Intro</p>
				<Button
					label="Intro afspelen"
					onClick={() => {
						setHasSeenIntro(false);
						navigate(-1);
					}}
				/>
			</div>
			<EditorialErrors />
		</main>
	);
};

const ChangeDateOptions = () => {
	const { currentDate, updateCurrentDate } = useContext(CurrentDateContext);

	return (
		<section>
			<h2 className="text-xl">Datum wijzigen</h2>
			<input
				type="date"
				value={currentDate.toISOString().split('T')[0]}
				onChange={(event) => {
					const newDate = Date.parse(event.target.value);
					if (!isNaN(newDate)) updateCurrentDate(new Date(newDate));
				}}
			/>
		</section>
	);
};

const BackupOptions = () => {
	const {
		backupProgress,
		backupStep,
		backupResults,
		backupSizesInMb,
		backupStoredOnNative,
		startBackup,
		shareBackup,
	} = useBackup();

	return (
		<section>
			<h2 className="text-xl">Backup maken</h2>
			{backupProgress === 0 ? (
				<Button label="Backup maken" onClick={startBackup} />
			) : (
				<>
					<p>
						Backup voortgang: {Math.round(backupProgress * 100)}% (
						{backupStep})
					</p>
					{backupProgress === 1 && (
						<div className="flex flex-col gap-2">
							<p>{backupResults.length} bestand(en) gereed:</p>
							{backupResults.map((_, i) => (
								<div
									key={i}
									className="flex items-center gap-3">
									<span>
										Backup {i + 1} -{' '}
										{backupSizesInMb[i]?.toFixed(2)} MB
										{backupStoredOnNative[i]
											? ' (lokaal opgeslagen)'
											: ''}
									</span>
									<Button
										label="Opslaan"
										onClick={() => shareBackup(i)}
									/>
								</div>
							))}
						</div>
					)}
				</>
			)}
		</section>
	);
};

const RestoreOptions = () => {
	const {
		restoreFromFiles,
		restoreProgress,
		restoreStep,
		restoredCount,
		skippedCount,
	} = useRestore();
	const inputRef = useRef<HTMLInputElement>(null);

	return (
		<section>
			<h2 className="text-xl">Backup terugzetten</h2>
			<input
				ref={inputRef}
				type="file"
				accept=".zip,application/zip"
				multiple
				onChange={(e) => {
					if (e.target.files && e.target.files.length > 0) {
						restoreFromFiles(e.target.files);
						// reset value to allow re-selecting the same files later
						e.currentTarget.value = '';
					}
				}}
				aria-hidden="true"
				className="hidden"
				tabIndex={-1}
			/>
			<div className="flex items-center gap-3">
				{restoreProgress === 0 && (
					<Button
						label="Backup terugzetten"
						onClick={() => {
							inputRef.current?.click();
						}}
					/>
				)}
				{restoreProgress > 0 && (
					<p>
						Herstellen: {Math.round(restoreProgress * 100)}% (
						{restoreStep}) - hersteld {restoredCount}, overgeslagen{' '}
						{skippedCount}
					</p>
				)}
			</div>
		</section>
	);
};

export default OptiesRoute;
